import {
  ScanLine,
  Heart,
  LineChart,
  UserPlus,
  Settings,
  Bell,
  LifeBuoy,
  Bot,
  BarChart3,
  Microscope,
  UserCog,
  Briefcase,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  id: string;
  label: string;
  labelKey: string; // i18n translation key
  mobileLabelKey?: string; // shorter i18n key for mobile bottom tabs
  icon: LucideIcon;
  path?: string;
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
  hideForAdmin?: boolean;
  disabled?: boolean; // e.g. Trading tab disabled for now
  beta?: boolean; // shows a Beta badge + bug-disclaimer tooltip
}

export const mainNavItems: NavItem[] = [
  {
    id: 'screening',
    label: 'Screening Results',
    labelKey: 'nav.screening',
    icon: ScanLine,
  },
  {
    id: 'charts',
    label: 'Charts',
    labelKey: 'nav.charts',
    icon: BarChart3,
  },
  {
    id: 'deep_analysis',
    label: 'Deep Analysis',
    labelKey: 'nav.deepAnalysis',
    icon: Microscope,
  },
  {
    id: 'favorites',
    label: 'Favorites',
    labelKey: 'nav.favorites',
    icon: Heart,
  },
  {
    id: 'alerts',
    label: 'Alerts',
    labelKey: 'nav.alerts',
    icon: Bell,
    requiresAuth: true,
    beta: true,
  },
  {
    id: 'positions',
    label: 'Trade Diary',
    labelKey: 'nav.positions',
    mobileLabelKey: 'nav.positionsShort',
    icon: Briefcase,
    requiresAuth: true,
    beta: true,
  },
  {
    id: 'settings',
    label: 'Settings',
    labelKey: 'nav.settings',
    icon: UserCog,
    requiresAuth: true,
  },
  {
    id: 'trading',
    label: 'Trading',
    labelKey: 'nav.trading',
    icon: LineChart,
    disabled: true, // AI Trading tab disabled for now
  },
  {
    id: 'roles',
    label: 'Request Roles',
    labelKey: 'nav.roles',
    icon: UserPlus,
    requiresAuth: true,
    hideForAdmin: true,
    disabled: true, // Role request feature disabled
  },
  {
    id: 'admin',
    label: 'Admin Hub',
    labelKey: 'nav.admin',
    icon: Settings,
    requiresAdmin: true,
  },
  {
    id: 'agents',
    label: 'Agent Console',
    labelKey: 'nav.agents',
    icon: Bot,
    requiresAuth: true,
    beta: true,
  },
  {
    id: 'help',
    label: 'Help',
    labelKey: 'nav.help',
    mobileLabelKey: 'nav.helpShort',
    icon: LifeBuoy,
  },
];

// MA Touch Alerts is always enabled in both development and production.
// The feature flag REACT_APP_ENABLE_MA_TOUCH_ALERTS is kept for backwards compatibility
// but the feature is now always active.
export const isMATouchAlertsEnabled = (): boolean => {
  // Always return true - alerts are enabled in all environments
  return true;
};

// Position Diary feature-enabled state - set once from the API probe
// (GET /api/positions/feature-status) at app boot / auth settle.
// Mirrors isMATouchAlertsEnabled() pattern.
let _positionDiaryEnabled: boolean | null = null;

export const isPositionDiaryEnabled = (): boolean => {
  // Null = not yet fetched; treat as disabled to prevent premature tab render.
  return _positionDiaryEnabled === true;
};

export const setPositionDiaryEnabled = (enabled: boolean): void => {
  _positionDiaryEnabled = enabled;
};

// Get mobile nav items with auth/admin parity to desktop sidebar.
// BUG-MA-005 FIX: Added Alerts tab to mobile nav
// BUG-MA-007 FIX: Added Charts tab to mobile nav
export const getMobileNavItems = (isAuthenticated: boolean = false, isAdmin: boolean = false): NavItem[] => {
  const items: NavItem[] = [
    {
      id: 'screening',
      label: 'Screening Results',
      labelKey: 'nav.screening',
      mobileLabelKey: 'nav.screeningShort',
      icon: ScanLine,
    },
    {
      id: 'charts',
      label: 'Charts',
      labelKey: 'nav.charts',
      icon: BarChart3,
    },
    {
      id: 'deep_analysis',
      label: 'Deep Analysis',
      labelKey: 'nav.deepAnalysis',
      mobileLabelKey: 'nav.deepAnalysisShort',
      icon: Microscope,
    },
    {
      id: 'favorites',
      label: 'Favorites',
      labelKey: 'nav.favorites',
      icon: Heart,
    },
  ];

  // Alerts tab: Beta, visible to authenticated users (may contain bugs).
  if (isMATouchAlertsEnabled()) {
    items.push({
      id: 'alerts',
      label: 'Alerts',
      labelKey: 'nav.alerts',
      icon: Bell,
      requiresAuth: true,
      beta: true,
    });
  }

  // Trade Diary tab: Beta, Pro-gated, visible when feature is enabled.
  if (isPositionDiaryEnabled()) {
    items.push({
      id: 'positions',
      label: 'Trade Diary',
      labelKey: 'nav.positions',
      mobileLabelKey: 'nav.positionsShort',
      icon: Briefcase,
      requiresAuth: true,
      beta: true,
    });
  }

  // Settings tab - authenticated users manage plan/account/preferences here.
  // Short label keeps the bottom-nav item narrow so it fits more easily.
  items.push({
    id: 'settings',
    label: 'Settings',
    labelKey: 'nav.settings',
    mobileLabelKey: 'nav.settingsShort',
    icon: UserCog,
    requiresAuth: true,
  });

  // Agent Console tab: Beta, visible to authenticated users (may contain bugs).
  items.push({
    id: 'agents',
    label: 'Agents',
    labelKey: 'nav.agents',
    mobileLabelKey: 'nav.agentsShort',
    icon: Bot,
    requiresAuth: true,
    beta: true,
  });

  if (isAdmin) {
    items.push({
      id: 'admin',
      label: 'Admin',
      labelKey: 'nav.admin',
      mobileLabelKey: 'nav.adminShort',
      icon: Settings,
      requiresAdmin: true,
    });
  }

  items.push({
    id: 'help',
    label: 'Help',
    labelKey: 'nav.help',
    mobileLabelKey: 'nav.helpShort',
    icon: LifeBuoy,
  });
  return items.filter((item) => {
    if (item.disabled) return false;
    if (item.requiresAuth && !isAuthenticated) return false;
    if (item.requiresAdmin && !isAdmin) return false;
    if (item.hideForAdmin && isAdmin) return false;
    return true;
  });
};

// BUG-MA-006 FIX: Get main nav items with feature flag filtering
export const getMainNavItems = (): NavItem[] => {
  return mainNavItems.filter(item => {
    // Filter out Alerts tab if feature is disabled
    if (item.id === 'alerts' && !isMATouchAlertsEnabled()) {
      return false;
    }
    // Filter out Trade Diary tab when feature is disabled (kill-switch or Pro gate)
    if (item.id === 'positions' && !isPositionDiaryEnabled()) {
      return false;
    }
    return true;
  });
};

// Legacy mobile nav items (deprecated - use getMobileNavItems instead)
export const mobileNavItems: NavItem[] = getMobileNavItems(true, true);

export type TabId =
  | 'screening'
  | 'favorites'
  | 'alerts'
  | 'positions'
  | 'settings'
  | 'billing' // legacy alias - no nav item; routed to Settings → Billing section
  | 'help'
  | 'trading'
  | 'roles'
  | 'admin'
  | 'agents'
  | 'charts'
  | 'deep_analysis';

// Shared so the App-level billing→settings alias and SettingsPage agree on the
// sessionStorage key that carries the requested settings sub-section.
export const SETTINGS_SECTION_STORAGE_KEY = 'settings_section_v1';

export const isValidTabId = (id: string): id is TabId => {
  return [
    'screening',
    'favorites',
    'alerts',
    'positions',
    'settings',
    'billing',
    'help',
    'trading',
    'roles',
    'admin',
    'agents',
    'charts',
    'deep_analysis',
  ].includes(id);
};
